import React, { useCallback, useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Cropper } from 'react-advanced-cropper';
import 'react-advanced-cropper/dist/style.css';
import './InteractiveCropOverlay.css';



const InteractiveCropOverlay = ({
  imageSrc,
  crop,
  onCropChange,
  aspectRatio,
  onAspectRatioChange,
  imageSize,
  isResizeMode = false,
  filterStyle = {},
  watermarkPreview = null,
}) => {
  const cropperRef = useRef(null);
  const isProgrammaticUpdateRef = useRef(false);
  const showGrid = true;

  const [stencilEl, setStencilEl] = useState(null);

  useEffect(() => {
    const timer = setInterval(() => {
      const el = document.querySelector('.cropper-stencil-custom');
      if (el) {
        setStencilEl(el);
        clearInterval(timer);
      }
    }, 100);
    return () => clearInterval(timer);
  }, []);

  // Initialize display size and offsets ONCE on mount to prevent cropper from re-initializing during parent updates
  const defaultSize = useRef({
    width: crop.width || imageSize.width || 800,
    height: crop.height || imageSize.height || 600,
  }).current;

  const defaultPosition = useRef({
    left: crop.x || 0,
    top: crop.y || 0,
  }).current;

  // Programmatic stencil snapping when parent coordinates (e.g. from Left Sidebar preset click) or aspectRatio change
  useEffect(() => {
    if (!cropperRef.current) return;

    // Skip programmatic updates if width or height are empty/falsy (e.g. user cleared inputs)
    if (!crop.width || !crop.height) return;

    const cropper = cropperRef.current;
    const currentCoords = cropper.getCoordinates();
    if (!currentCoords) return;

    // Check if the parent coordinates differ significantly from local coordinates
    const targetWidth = crop.width;
    const targetHeight = crop.height;

    const targetLeft = crop.x || 0;
    const targetTop = crop.y || 0;
    const widthDiff = Math.abs(currentCoords.width - targetWidth);
    const heightDiff = Math.abs(currentCoords.height - targetHeight);
    const leftDiff = Math.abs(currentCoords.left - targetLeft);
    const topDiff = Math.abs(currentCoords.top - targetTop);

    // Also check if current coordinates aspect ratio matches the new aspect ratio
    const currentRatio = currentCoords.width / currentCoords.height;
    const ratioMismatch = aspectRatio && Math.abs(currentRatio - aspectRatio) > 0.02;

    if (widthDiff > 5 || heightDiff > 5 || leftDiff > 5 || topDiff > 5 || ratioMismatch) {
      // Outside update detected! Adjust the cropper coordinates.
      const image = cropper.getImage();
      
      if (image) {
        const imageWidth = image.width;
        const imageHeight = image.height;

        let finalWidth = targetWidth;
        let finalHeight = targetHeight;

        // If aspect ratio is specified, ensure it is strictly respected
        if (aspectRatio) {
          if (widthDiff > 5 || heightDiff > 5) {
            // Keep targetWidth but adjust targetHeight to match aspectRatio
            finalHeight = finalWidth / aspectRatio;
            
            // If it exceeds image dimensions, scale down
            if (finalHeight > imageHeight) {
              finalHeight = imageHeight;
              finalWidth = imageHeight * aspectRatio;
            }
            if (finalWidth > imageWidth) {
              finalWidth = imageWidth;
              finalHeight = imageWidth / aspectRatio;
            }
          } else {
            // Only aspect ratio changed; adjust box to fit the new ratio within current boundary
            if (currentRatio > aspectRatio) {
              // Current stencil is wider than new ratio: reduce width
              finalWidth = currentCoords.height * aspectRatio;
              finalHeight = currentCoords.height;
            } else {
              // Current stencil is taller than new ratio: reduce height
              finalHeight = currentCoords.width / aspectRatio;
              finalWidth = currentCoords.width;
            }
          }
        }

        const nextLeft = (leftDiff > 5 || topDiff > 5)
          ? targetLeft
          : (imageWidth - finalWidth) / 2;
        const nextTop = (leftDiff > 5 || topDiff > 5)
          ? targetTop
          : (imageHeight - finalHeight) / 2;

        const left = Math.max(0, Math.min(Math.round(imageWidth - finalWidth), Math.round(nextLeft)));
        const top = Math.max(0, Math.min(Math.round(imageHeight - finalHeight), Math.round(nextTop)));

        const newCoords = {
          width: Math.round(finalWidth),
          height: Math.round(finalHeight),
          left,
          top,
        };

        // Acquire lock before setCoordinates to prevent local triggers from updating parent state
        isProgrammaticUpdateRef.current = true;
        cropper.setCoordinates(newCoords);
        setTimeout(() => {
          isProgrammaticUpdateRef.current = false;
        }, 0);
      }
    }
  }, [crop.x, crop.y, crop.width, crop.height, aspectRatio, imageSize]);

  // Track coordinates locally in real-time inside the cropper context
  const handleCropperChange = useCallback((cropperInstance) => {
    // If the update was triggered programmatically (e.g. typing in Sidebar fields), skip triggering onChange to the parent
    if (isProgrammaticUpdateRef.current) return;

    const coords = cropperInstance.getCoordinates();
    if (coords) {
      const width = Math.round(coords.width);
      const height = Math.round(coords.height);
      
      onCropChange({
        x: Math.max(0, Math.round(coords.left)),
        y: Math.max(0, Math.round(coords.top)),
        width,
        height,
      });
    }
  }, [onCropChange]);

  // Dynamic sizing restrictions based on the transformed image dimensions
  const handleSizeRestrictions = useCallback((state) => {
    const transformedImageSize = state?.imageSize;
    if (!transformedImageSize || !transformedImageSize.width || !transformedImageSize.height) {
      return { minWidth: 64, minHeight: 64, maxWidth: Infinity, maxHeight: Infinity };
    }

    // Calculate dynamic minimums based on transformed image dimensions (e.g. 5% minimum threshold)
    const minW = Math.max(64, Math.round(transformedImageSize.width * 0.05));
    const minH = Math.max(64, Math.round(transformedImageSize.height * 0.05));

    return {
      minWidth: minW,
      minHeight: minH,
      maxWidth: transformedImageSize.width,
      maxHeight: transformedImageSize.height,
    };
  }, []);




  return (
    <div className="crop-overlay-container">
      {/* Main Cropper Stage */}
      <div className="crop-area" style={filterStyle}>
        <Cropper
          ref={cropperRef}
          src={imageSrc}
          className="advanced-cropper-instance"
          defaultSize={() => defaultSize}
          defaultPosition={() => defaultPosition}
          onChange={handleCropperChange}
          transitions={true}
          sizeRestrictions={handleSizeRestrictions}
          stencilProps={{
            aspectRatio: aspectRatio || undefined,
            grid: showGrid,
            overlayClassName: 'cropper-overlay-custom',
            className: 'cropper-stencil-custom'
          }}
        />
        {stencilEl && watermarkPreview && createPortal(
          watermarkPreview,
          stencilEl
        )}
      </div>
    </div>
  );
};

export default InteractiveCropOverlay;
